import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import {
  Bug,
  Check,
  Film,
  ImagePlus,
  Lightbulb,
  Loader2,
  MapPin,
  MessageSquarePlus,
  Plus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useBugReportConfig, useReportBug, type BugReportType } from "@/hooks/use-bug-reports";
import type { UploadProgress } from "@/lib/api";
import {
  MAX_ATTACHMENTS,
  MAX_IMAGE_BYTES,
  MAX_TOTAL_BYTES,
  MAX_VIDEO_BYTES,
  isImageType,
  validateAttachmentList,
} from "@/lib/bug-attachments";
import { cn } from "@/lib/utils";

const TITLE_MAX = 200;

const TYPES: {
  value: BugReportType;
  label: string;
  hint: string;
  icon: typeof Bug;
  titlePlaceholder: string;
  descriptionPlaceholder: string;
  submit: string;
  sent: string;
}[] = [
  {
    value: "bug",
    label: "Bug",
    hint: "Nešto ne radi kako treba",
    icon: Bug,
    titlePlaceholder: "Kratko: šta ne radi",
    descriptionPlaceholder: "Koje korake si radio, šta si očekivao, šta se desilo umjesto toga",
    submit: "Pošalji bug",
    sent: "Bug poslan, hvala!",
  },
  {
    value: "prijedlog",
    label: "Prijedlog",
    hint: "Nešto bi moglo biti bolje",
    icon: Lightbulb,
    titlePlaceholder: "Kratko: šta bi dodali",
    descriptionPlaceholder: "Šta bi ti olakšalo posao i kada bi to koristio",
    submit: "Pošalji prijedlog",
    sent: "Prijedlog poslan, hvala!",
  },
];

type Attachment = {
  key: string;
  file: File;
  /** Object URL za sličicu (slika ili prvi kadar videa); oslobađa se pri uklanjanju. */
  previewUrl: string;
};

const IMAGE_EXT: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
};

const MB = 1024 * 1024;

const LIMITS_HINT =
  `Do ${MAX_ATTACHMENTS} fajlova · slike do ${MAX_IMAGE_BYTES / MB} MB · ` +
  `video do ${MAX_VIDEO_BYTES / MB} MB`;

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const PASTE_KEYS = IS_MAC ? "⌘V" : "Ctrl+V";
const SUBMIT_KEYS = IS_MAC ? "⌘↵" : "Ctrl+Enter";

function formatMb(bytes: number): string {
  return (bytes / MB).toFixed(1).replace(".", ",");
}

function formatSize(bytes: number): string {
  if (bytes < MB) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${formatMb(bytes)} MB`;
}

/**
 * Udio (0–1) svakog fajla koji je već poslan. Svi prilozi idu u jednom zahtjevu,
 * redom kojim su dodani, pa se ukupni napredak dijeli po njihovim veličinama.
 */
function perFileProgress(sizes: number[], fraction: number): number[] {
  const total = sizes.reduce((sum, n) => sum + n, 0);
  let sentBytes = fraction * total;
  return sizes.map((size) => {
    const part = size === 0 ? 1 : Math.min(1, Math.max(0, sentBytes / size));
    sentBytes -= size;
    return part;
  });
}

function isEditable(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

/**
 * Fiksno dugme u donjem desnom uglu, vidljivo na svakom ekranu na desktopu.
 * Na mobitelu je skriveno — tamo bi zaklanjalo akcije u skeniranju i pakovanju.
 */
export function BugReportButton() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<BugReportType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [dragging, setDragging] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const reportBug = useReportBug();
  const { data: config } = useBugReportConfig();
  const attachmentsEnabled = config?.attachmentsEnabled ?? false;
  const pending = reportBug.isPending;
  const copy = TYPES.find((t) => t.value === type)!;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Paste listener je na documentu pa mu treba svježa lista mimo closurea.
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  const pasteCounter = useRef(0);
  // dragenter/dragleave se okidaju i za svako dijete — brojimo dubinu da highlight ne treperi.
  const dragDepth = useRef(0);

  // Pri unmountu oslobodi sve preostale sličice.
  useEffect(() => {
    return () => {
      for (const a of attachmentsRef.current) URL.revokeObjectURL(a.previewUrl);
    };
  }, []);

  function addFiles(incoming: File[]) {
    if (incoming.length === 0 || pending) return;
    const current = attachmentsRef.current;
    const error = validateAttachmentList([...current.map((a) => a.file), ...incoming]);
    setFileError(error);
    if (error) return;
    const added = incoming.map((file) => ({
      key: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    const next = [...current, ...added];
    attachmentsRef.current = next;
    setAttachments(next);
  }

  function removeAttachment(key: string) {
    const target = attachmentsRef.current.find((a) => a.key === key);
    if (target) URL.revokeObjectURL(target.previewUrl);
    const next = attachmentsRef.current.filter((a) => a.key !== key);
    attachmentsRef.current = next;
    setAttachments(next);
    setFileError(null);
  }

  // Ctrl+V screenshota dok je dijalog otvoren. Presrećemo samo kad clipboard nosi fajlove,
  // a tekst lijepljen u polje (npr. iz Excela, koji uz tekst nosi i sliku) ostavljamo na miru.
  useEffect(() => {
    if (!open || !attachmentsEnabled) return;
    function onPaste(e: ClipboardEvent) {
      const data = e.clipboardData;
      if (!data || data.files.length === 0) return;
      if (data.types.includes("text/plain") && isEditable(e.target)) return;
      e.preventDefault();
      const files = Array.from(data.files).map((f) => {
        const ext = IMAGE_EXT[f.type];
        if (!ext) return f;
        pasteCounter.current += 1;
        return new File([f], `screenshot-${pasteCounter.current}.${ext}`, { type: f.type });
      });
      addFiles(files);
    }
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
    // Bez deps namjerno: listener se obnavlja svakim renderom pa vidi svjež addFiles (isPending).
  });

  function hasFiles(e: DragEvent) {
    return e.dataTransfer.types.includes("Files");
  }

  const dropHandlers = attachmentsEnabled
    ? {
        onDragEnter: (e: DragEvent) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        },
        onDragOver: (e: DragEvent) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        },
        onDragLeave: (e: DragEvent) => {
          if (!hasFiles(e)) return;
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragging(false);
        },
        onDrop: (e: DragEvent) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          dragDepth.current = 0;
          setDragging(false);
          addFiles(Array.from(e.dataTransfer.files));
        },
      }
    : {};

  function reset() {
    setType("bug");
    setTitle("");
    setDescription("");
    for (const a of attachmentsRef.current) URL.revokeObjectURL(a.previewUrl);
    attachmentsRef.current = [];
    setAttachments([]);
    pasteCounter.current = 0;
    setTitleError(false);
    setFileError(null);
    setSubmitError(null);
  }

  async function handleSubmit() {
    if (pending) return;
    if (!title.trim()) {
      setTitleError(true);
      titleRef.current?.focus();
      return;
    }
    setSubmitError(null);
    setFileError(null);
    const files = attachmentsEnabled ? attachments.map((a) => a.file) : [];
    // Traka kreće od 0% odmah, ne tek na prvi javljeni dio uploada.
    if (files.length > 0) setProgress({ fraction: 0, sent: false });
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await reportBug.mutateAsync({
        type,
        title: title.trim(),
        description: description.trim(),
        // Puna putanja s koje je prijava poslana — bez nje je opis rijetko dovoljan.
        pageUrl: window.location.pathname + window.location.search,
        files,
        onProgress: setProgress,
        signal: controller.signal,
      });
      toast.success(copy.sent);
      setOpen(false);
      reset();
    } catch (e: any) {
      // Prekid je korisnikov izbor — nacrt ostaje netaknut, bez poruke o grešci.
      if (e?.name !== "AbortError") setSubmitError(e?.message || "Greška pri slanju prijave");
    } finally {
      abortRef.current = null;
      setProgress(null);
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSubmit();
    }
  }

  function handleTypeKeys(e: KeyboardEvent<HTMLDivElement>) {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
    e.preventDefault();
    const next = type === "bug" ? "prijedlog" : "bug";
    setType(next);
    e.currentTarget.querySelector<HTMLElement>(`[data-type="${next}"]`)?.focus();
  }

  const totalBytes = attachments.reduce((sum, a) => sum + a.file.size, 0);
  const uploading = pending && progress !== null;
  // Kad je tijelo poslano, server još prebacuje priloge u pohranu — tu više nema procenta.
  const processing = pending && (progress === null || progress.sent);
  const fileProgress = uploading
    ? perFileProgress(
        attachments.map((a) => a.file.size),
        progress.fraction,
      )
    : null;
  const percent = uploading ? Math.round(progress.fraction * 100) : 0;
  const pagePath = typeof window === "undefined" ? "" : window.location.pathname + window.location.search;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="hidden md:flex fixed bottom-6 right-6 z-50 shadow-lg print:hidden"
      >
        <MessageSquarePlus className="h-4 w-4" />
        Bug / Prijedlog
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          // Dok slanje traje dijalog se ne zatvara — prvo „Prekini slanje".
          if (!next && pending) return;
          setOpen(next);
        }}
      >
        <DialogContent
          className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl"
          onKeyDown={handleKeyDown}
          {...dropHandlers}
        >
          {dragging && (
            <div className="pointer-events-none absolute inset-0 z-20 bg-background/90 p-3">
              <div className="flex h-full flex-col items-center justify-center gap-2 border-2 border-dashed border-foreground">
                <ImagePlus className="size-6" />
                <p className="text-sm font-medium">Pusti fajlove da ih priložiš</p>
                <p className="text-xs text-muted-foreground">{LIMITS_HINT}</p>
              </div>
            </div>
          )}

          <DialogHeader className="gap-1.5 border-b px-6 py-5 pr-12 text-left">
            <DialogTitle className="text-lg font-semibold tracking-tight">
              Prijavi bug ili prijedlog
            </DialogTitle>
            <DialogDescription>
              Prijava ide direktno timu koji održava aplikaciju.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
            <div
              role="radiogroup"
              aria-label="Vrsta prijave"
              className="grid grid-cols-2"
              onKeyDown={handleTypeKeys}
            >
              {TYPES.map((t) => {
                const selected = type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-type={t.value}
                    tabIndex={selected ? 0 : -1}
                    disabled={pending}
                    onClick={() => setType(t.value)}
                    className={cn(
                      "flex items-center gap-3 border px-4 py-3 text-left outline-none transition-colors",
                      "first:border-r-0 focus-visible:z-10 focus-visible:ring-[3px] focus-visible:ring-ring/50",
                      "disabled:pointer-events-none disabled:opacity-60",
                      selected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-input text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <t.icon className="size-5 shrink-0" />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">{t.label}</span>
                      <span className={cn("block truncate text-xs", selected && "opacity-70")}>
                        {t.hint}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="bug-title">Naslov</Label>
                <span
                  className={cn(
                    "text-[11px] tabular-nums text-muted-foreground",
                    title.length >= TITLE_MAX && "text-destructive",
                  )}
                >
                  {title.length}/{TITLE_MAX}
                </span>
              </div>
              <Input
                id="bug-title"
                ref={titleRef}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setTitleError(false);
                }}
                placeholder={copy.titlePlaceholder}
                maxLength={TITLE_MAX}
                disabled={pending}
                aria-invalid={titleError}
                aria-describedby={titleError ? "bug-title-error" : undefined}
                className="h-10 px-3 text-sm"
                autoFocus
              />
              {titleError && (
                <p id="bug-title-error" role="alert" className="text-xs text-destructive">
                  Upiši naslov — jedna rečenica je dovoljna.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="bug-description">Opis</Label>
                <span className="text-[11px] text-muted-foreground">nije obavezan</span>
              </div>
              <Textarea
                id="bug-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={copy.descriptionPlaceholder}
                maxLength={10000}
                disabled={pending}
                className="max-h-64 min-h-32 rounded-none px-3 py-2.5 text-sm leading-relaxed shadow-none"
              />
            </div>

            {attachmentsEnabled && (
              <div className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <Label>Prilozi</Label>
                  <span className="text-[11px] tabular-nums text-muted-foreground">
                    {attachments.length}/{MAX_ATTACHMENTS}
                    {attachments.length > 0 &&
                      ` · ${formatMb(totalBytes)} od ${MAX_TOTAL_BYTES / MB} MB`}
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={(e) => {
                    addFiles(Array.from(e.target.files ?? []));
                    // Isti fajl se može ponovo izabrati nakon uklanjanja.
                    e.target.value = "";
                  }}
                />

                {attachments.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={pending}
                    className="flex w-full items-center gap-4 border border-dashed border-input px-4 py-5 text-left outline-none transition-colors hover:border-foreground/40 hover:bg-accent/60 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-60"
                  >
                    <span className="flex size-10 shrink-0 items-center justify-center bg-muted">
                      <ImagePlus className="size-5 text-muted-foreground" />
                    </span>
                    <span className="min-w-0 space-y-1">
                      <span className="block text-sm font-medium">
                        Dodaj sliku ili video
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        Klikni, prevuci fajlove ovdje ili zalijepi screenshot ({PASTE_KEYS})
                      </span>
                    </span>
                  </button>
                ) : (
                  <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                    {attachments.map((a, i) => {
                      const sent = fileProgress?.[i] ?? 0;
                      return (
                        <li key={a.key} className="min-w-0">
                          <div className="relative aspect-square overflow-hidden border bg-muted">
                            {isImageType(a.file.type) ? (
                              <img
                                src={a.previewUrl}
                                alt=""
                                className="size-full object-cover"
                              />
                            ) : (
                              <>
                                <Film className="absolute inset-0 m-auto size-5 text-muted-foreground" />
                                {/* Prvi kadar kao sličica; ako browser ne zna format, ostaje ikona. */}
                                <video
                                  src={a.previewUrl}
                                  muted
                                  playsInline
                                  preload="metadata"
                                  className="relative size-full object-cover"
                                />
                                <span className="absolute bottom-1 left-1 bg-foreground px-1 text-[10px] font-medium uppercase text-background">
                                  Video
                                </span>
                              </>
                            )}
                            {uploading ? (
                              <>
                                {/* Veo koji se povlači prema gore kako fajl odlazi na server. */}
                                <div
                                  className="absolute inset-x-0 top-0 bg-background/75 motion-safe:transition-[height] motion-safe:duration-200"
                                  style={{ height: `${(1 - sent) * 100}%` }}
                                />
                                {sent >= 1 && (
                                  <span className="absolute right-1 top-1 flex size-5 items-center justify-center bg-foreground text-background">
                                    <Check className="size-3" />
                                  </span>
                                )}
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => removeAttachment(a.key)}
                                disabled={pending}
                                aria-label={`Ukloni ${a.file.name}`}
                                className="absolute right-1 top-1 flex size-6 items-center justify-center border bg-background outline-none transition-colors hover:bg-foreground hover:text-background focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:hidden"
                              >
                                <X className="size-3.5" />
                              </button>
                            )}
                          </div>
                          <p className="mt-1 truncate text-[11px]" title={a.file.name}>
                            {a.file.name}
                          </p>
                          <p className="text-[11px] tabular-nums text-muted-foreground">
                            {formatSize(a.file.size)}
                          </p>
                        </li>
                      );
                    })}
                    {attachments.length < MAX_ATTACHMENTS && !pending && (
                      <li>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          aria-label="Dodaj još priloga"
                          className="flex aspect-square w-full items-center justify-center border border-dashed border-input text-muted-foreground outline-none transition-colors hover:border-foreground/40 hover:bg-accent/60 hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
                        >
                          <Plus className="size-5" />
                        </button>
                      </li>
                    )}
                  </ul>
                )}

                {fileError ? (
                  <p role="alert" className="text-xs text-destructive">
                    {fileError}
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">{LIMITS_HINT}</p>
                )}
              </div>
            )}
          </div>

          <div className="relative border-t bg-muted/40 px-6 py-4">
            {pending && (
              <div
                role="progressbar"
                aria-label="Slanje prijave"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={processing ? undefined : percent}
                className="absolute inset-x-0 -top-px h-[3px] overflow-hidden bg-border"
              >
                {processing ? (
                  <div className="h-full w-1/4 bg-foreground motion-safe:animate-[slide_1.2s_linear_infinite] motion-reduce:w-full" />
                ) : (
                  <div
                    className="h-full bg-foreground motion-safe:transition-[width] motion-safe:duration-200"
                    style={{ width: `${percent}%` }}
                  />
                )}
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 text-xs" aria-live="polite">
                {pending ? (
                  processing ? (
                    <p className="font-medium">Spremanje prijave…</p>
                  ) : (
                    <p className="tabular-nums">
                      <span className="font-medium">Slanje priloga {percent}%</span>
                      <span className="text-muted-foreground">
                        {" · "}
                        {formatMb(progress!.fraction * totalBytes)} / {formatMb(totalBytes)} MB
                      </span>
                    </p>
                  )
                ) : submitError ? (
                  <p role="alert" className="text-destructive">
                    Prijava nije poslana: {submitError}
                  </p>
                ) : (
                  <p className="flex items-center gap-1.5 text-muted-foreground">
                    <MapPin className="size-3.5 shrink-0" />
                    <span className="shrink-0">Šalje se uz stranicu</span>
                    <span className="truncate text-foreground" title={pagePath}>
                      {pagePath}
                    </span>
                  </p>
                )}
              </div>

              <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row">
                {pending ? (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => abortRef.current?.abort()}
                    disabled={processing}
                  >
                    Prekini slanje
                  </Button>
                ) : (
                  <Button variant="outline" size="lg" onClick={() => setOpen(false)}>
                    Odustani
                  </Button>
                )}
                <Button size="lg" onClick={handleSubmit} disabled={pending}>
                  {pending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {copy.submit}
                  {!pending && (
                    <kbd className="ml-1 hidden text-[10px] opacity-60 sm:inline">{SUBMIT_KEYS}</kbd>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
